import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits
} from '../../../../types';
import {
  FetchTeamsUsersCacheKey,
  FetchCacheTeamsUsersFilters,
  FetchTeamsUsersSort,
  FetchTeamsUsersPage,
  FetchTeamsUsersLimit,
  FetchTeamsUsersGqlQuery
} from '../../teamsUsersTypes';

import { useCacheInfiniteIndexQuery } from '../../../common/reactQuery/hooks/useCacheInfiniteIndexQuery';

import {
  INITIAL_TEAMS_USERS_FILTERS,
  INITIAL_TEAMS_USERS_LIMIT,
  INITIAL_TEAMS_USERS_SORT
} from '../../teamsUsersConstants';
import { InfiniteIndexQueryBaseNodeType } from '../../../common/reactQuery/hooks/useInfiniteIndexQuery';

interface TeamsUsersOptions {
  action?: string;
  cacheKey: FetchTeamsUsersCacheKey;
  query: FetchTeamsUsersGqlQuery;
  initialFilters?: FetchCacheTeamsUsersFilters;
  initialSort?: FetchTeamsUsersSort;
  initialPage?: FetchTeamsUsersPage;
  initialLimit?: FetchTeamsUsersLimit;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
}

const scope = 'teamsUsers';

function useCacheTeamsUsers<
  FetchTeamsUsersType extends InfiniteIndexQueryBaseNodeType
>({
  action,
  cacheKey,
  initialFilters = INITIAL_TEAMS_USERS_FILTERS,
  initialSort = INITIAL_TEAMS_USERS_SORT,
  initialLimit = INITIAL_TEAMS_USERS_LIMIT,
  query,
  source = FetchCacheItemsSources.DB,
  trackTotalHits,
  options = {}
}: TeamsUsersOptions) {
  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    isFetched,
    isLoading,
    isFetchingNextPage,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    hasNextPage,
    updateItemCache,
    loadMoreItems,
    filterItems,
    changeItemsFilters,
    clearItemsFilters,
    clearItemsFiltersPersistInitial,
    sortItems,
    limitItems,
    prefetchItem
  } = useCacheInfiniteIndexQuery<FetchTeamsUsersType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    initialFilters,
    initialLimit,
    initialSort,
    options,
    query,
    scope,
    source,
    trackTotalHits
  });

  return {
    teamsUsersData: data,
    teamsUsers: items,
    teamsUsersError: itemsError,
    teamsUsersTotalCount: itemsTotalCount,
    teamsUsersFetched: isFetched,
    teamsUsersLoading: isLoading,
    teamsUsersFetchingNextPage: isFetchingNextPage,
    teamsUsersIsPlaceholderData: isPlaceholderData,
    teamsUsersFilters: currentFilters,
    teamsUsersSort: currentSort,
    teamsUsersPage: currentPage,
    teamsUsersLimit: currentLimit,
    hasNextTeamsUsersPage: hasNextPage,
    updateTeamsUserCache: updateItemCache,
    loadMoreTeamsUsers: loadMoreItems,
    filterTeamsUsers: filterItems,
    changeTeamsUsersFilters: changeItemsFilters,
    clearTeamsUsersFilters: clearItemsFilters,
    clearTeamsUsersFiltersPersistInitial: clearItemsFiltersPersistInitial,
    sortTeamsUsers: sortItems,
    limitTeamsUsers: limitItems,
    prefetchTeamsUser: prefetchItem
  };
}

export default useCacheTeamsUsers;
